import { Component } from '@theme/component';
import { ThemeEvents, CartAddEvent } from '@theme/events';
import { DialogComponent, DialogCloseEvent } from '@theme/dialog';
import { mediaQueryLarge, onAnimationEnd } from '@theme/utilities';

/**
 * Syncs data-option-available and aria-disabled from a response variant-picker to DOM inputs.
 * @param {Element} sourcePicker - The variant-picker from the fetch response
 * @param {Element} targetPicker - The variant-picker in the DOM to update
 */
function syncAvailability(sourcePicker, targetPicker) {
  /** @type {Map<string, string>} */
  const availabilityMap = new Map();
  for (const input of sourcePicker.querySelectorAll('input[data-option-value-id]')) {
    const id = /** @type {HTMLElement} */ (input).dataset.optionValueId;
    const available = /** @type {HTMLElement} */ (input).dataset.optionAvailable;
    if (id && available !== undefined) availabilityMap.set(id, available);
  }
  for (const input of targetPicker.querySelectorAll('input[data-option-value-id]')) {
    const id = /** @type {HTMLElement} */ (input).dataset.optionValueId;
    if (!id || !availabilityMap.has(id)) continue;
    /** @type {HTMLElement} */ (input).dataset.optionAvailable = /** @type {string} */ (availabilityMap.get(id));
    if (availabilityMap.get(id) === 'false') {
      input.setAttribute('aria-disabled', 'true');
    } else {
      input.removeAttribute('aria-disabled');
    }
  }
}

/**
 * Quick add block component.
 * Handles ATC button click: opens per-card dialog (modal mode) or toggles overlay (overlay mode).
 * Content is server-rendered — no fetch/morph needed.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} [overlay]
 * @property {HTMLElement} [overlayContent]
 * @extends {Component<Refs>}
 */
export class QuickAddBlock extends Component {
  /** @type {AbortController | null} */
  #overlayAbortController = null;

  /** @type {AbortController | null} */
  #fetchAbortController = null;

  /** @type {Element | null} */
  #productCard = null;

  /** @type {string | null} Product ID currently loaded in the variant picker fieldsets */
  #loadedProductId = null;

  get quickAddStyle() {
    return this.dataset.quickAddStyle || 'modal';
  }

  connectedCallback() {
    super.connectedCallback();
    mediaQueryLarge.addEventListener('change', this.#closeOnViewportChange);

    // Listen for variant changes from sibling swatch pickers on the parent product-card
    const productCard = this.closest('product-card');
    if (productCard) {
      this.#productCard = productCard;
      productCard.addEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUpdate);
    }

    // Track which product's data is currently loaded in the variant picker fieldsets
    const firstPicker = /** @type {HTMLElement | null} */ (this.querySelector('variant-picker'));
    if (firstPicker) {
      this.#loadedProductId = firstPicker.dataset.productId || null;
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    mediaQueryLarge.removeEventListener('change', this.#closeOnViewportChange);
    this.#overlayAbortController?.abort();
    this.#fetchAbortController?.abort();
    this.#productCard?.removeEventListener(ThemeEvents.variantUpdate, this.#handleCardVariantUpdate);
  }

  /**
   * Handles card-level swatch changes: close overlay, sync product context, and update forms.
   * Also syncs card-level forms immediately so that single-variant products (which have no
   * modal/overlay and thus never call #syncToCurrentSwatch) get their ATC button updated.
   *
   * @param {CustomEvent} event - The variant:update event from the swatch picker
   */
  #handleCardVariantUpdate = (event) => {
    // Ignore events originating from within this quick-add block (overlay/modal variant picker)
    if (this.contains(/** @type {Node} */ (event.target))) return;
    this.closeOverlay();

    // Sync product URL/ID from the swatch picker (for combined listings)
    this.#syncProductContext();

    // Sync card-level form inputs and ATC button state from the event's variant data
    const variant = event.detail?.resource;
    if (variant) {
      this.#syncFormsToVariant({ id: variant.id, available: variant.available });
    }
  };

  /**
   * Syncs modal/overlay forms to the currently checked swatch on the product card.
   * Reads the swatch's variant ID and availability, updates hidden inputs and ATC buttons,
   * and injects the swatch option value into variant pickers for correct fetches.
   */
  #syncToCurrentSwatch() {
    if (!this.#productCard) return;

    const checkedSwatch = /** @type {HTMLInputElement | null} */ (
      this.#productCard.querySelector('swatches-variant-picker-component input[type="radio"]:checked')
    );
    if (!checkedSwatch) return;

    // For combined listings (product siblings), the swatch may point to a different product.
    // Sync the product URL and ID so variant picker fetches and form submissions use the correct product.
    this.#syncProductContext();

    // Resolve variant ID and availability from the swatch input
    const variantId = checkedSwatch.dataset.firstAvailableOrFirstVariantId || checkedSwatch.dataset.variantId;
    const available = checkedSwatch.dataset.optionAvailable !== 'false';

    if (variantId) {
      this.#syncFormsToVariant({ id: variantId, available });
    }

    // Inject the swatch option value so variant picker fetches include the color
    const optionValueId = checkedSwatch.dataset.optionValueId;
    if (optionValueId) {
      this.#injectSwatchOptionValue(optionValueId);
    }

    // Update modal image immediately from card's current image
    this.#syncModalImage();

    // Fetch fresh content from server to update Size availability and JSON
    this.#fetchAndSyncContent();
  }

  /**
   * Syncs product URL and ID from the card's swatch picker to the modal/overlay components.
   * Required for combined listings (product siblings) where each color is a different product.
   */
  #syncProductContext() {
    if (!this.#productCard) return;

    const cardSwatchPicker = this.#productCard.querySelector('swatches-variant-picker-component');
    if (!cardSwatchPicker) return;

    const newProductUrl = /** @type {HTMLElement} */ (cardSwatchPicker).dataset.productUrl;
    const newProductId = /** @type {HTMLElement} */ (cardSwatchPicker).dataset.productId;
    if (!newProductUrl) return;

    for (const picker of this.querySelectorAll('variant-picker')) {
      /** @type {HTMLElement} */ (picker).dataset.productUrl = newProductUrl;
      if (newProductId) /** @type {HTMLElement} */ (picker).dataset.productId = newProductId;
    }

    if (newProductId) {
      for (const form of this.querySelectorAll('product-form-component')) {
        /** @type {HTMLElement} */ (form).dataset.productId = newProductId;
      }
    }
  }

  /**
   * Updates all quick-add forms to reflect the given variant.
   * Sets hidden variant ID inputs and enables/disables ATC buttons.
   *
   * @param {{ id: string | number, available: boolean }} variant
   */
  #syncFormsToVariant(variant) {
    const variantInputs = /** @type {NodeListOf<HTMLInputElement>} */ (
      this.querySelectorAll('input[name="id"][ref="variantId"]')
    );

    for (const input of variantInputs) {
      input.value = String(variant.id);
      input.setAttribute('value', String(variant.id));
      if (variant.available === false) {
        input.setAttribute('disabled', '');
      } else {
        input.removeAttribute('disabled');
      }
    }

    const addToCartContainers = /** @type {NodeListOf<HTMLElement>} */ (
      this.querySelectorAll('add-to-cart-component')
    );

    for (const container of addToCartContainers) {
      if (variant.available === false) {
        if (typeof /** @type {any} */ (container).disable === 'function') {
          /** @type {any} */ (container).disable();
        }
      } else {
        if (typeof /** @type {any} */ (container).enable === 'function') {
          /** @type {any} */ (container).enable();
        }
      }
    }

    // Also handle bare submit buttons (single-variant products without add-to-cart-component wrapper)
    const submitButtons = /** @type {NodeListOf<HTMLButtonElement>} */ (
      this.querySelectorAll('button[name="add"][type="submit"]')
    );
    for (const button of submitButtons) {
      button.disabled = variant.available === false;
    }
  }

  /**
   * Injects a hidden swatch option value into the variant pickers inside this quick-add block.
   * Only injects when the modal lacks a visible input for this option (sibling products
   * where the color is a swatch excluded from the modal's variant picker).
   * Skips injection when the modal already has a visible color fieldset (regular variants).
   *
   * @param {string} optionValueId - The swatch option value ID to inject
   */
  #injectSwatchOptionValue(optionValueId) {
    const variantPickers = this.querySelectorAll('variant-picker');

    for (const picker of variantPickers) {
      // Skip if the picker already has a visible input for this option
      if (picker.querySelector(`input[data-option-value-id="${optionValueId}"]`)) continue;

      const form = picker.querySelector('form');
      if (!form) continue;

      const fieldset = document.createElement('fieldset');
      fieldset.className = 'quick-add-swatch-option';
      fieldset.hidden = true;
      fieldset.innerHTML = `<input type="radio" checked data-option-value-id="${optionValueId}">`;
      form.prepend(fieldset);
    }
  }

  /**
   * Fetches fresh content from section-rendering-quick-add and updates the modal/overlay:
   * - Size button availability (data-option-available attributes)
   * - Variant picker JSON script tag
   * - Form hidden variant ID input and ATC button state
   * - Cart notification template
   * - Price display
   *
   * For combined listings (product siblings), the fetched product may differ from what's
   * currently in the DOM. In that case, the entire variant-picker fieldsets are replaced
   * because option value IDs are product-specific and cannot be matched across products.
   */
  #fetchAndSyncContent() {
    if (!this.#productCard) return;

    // Find variant pickers inside this quick-add block (modal and/or overlay)
    const variantPickers = this.querySelectorAll('variant-picker');
    if (variantPickers.length === 0) return;

    // Build the fetch URL using the first variant picker's product URL and current option values
    const picker = /** @type {import('@theme/variant-picker').default} */ (variantPickers[0]);
    const productUrl = picker.dataset.productUrl;
    if (!productUrl) return;

    // Check if the product has changed (combined listings) before collecting option values.
    // If the product changed, the existing size option value IDs belong to the old product
    // and must not be sent — only send the injected swatch option value.
    const cardSwatchPicker = this.#productCard.querySelector('swatches-variant-picker-component');
    const currentProductId = cardSwatchPicker
      ? /** @type {HTMLElement} */ (cardSwatchPicker).dataset.productId
      : null;
    const isProductChanging = currentProductId && currentProductId !== this.#loadedProductId;

    let optionValueIds;
    if (isProductChanging) {
      // Product URL encodes the color — let selected_or_first_available_variant pick the size
      optionValueIds = [];
    } else {
      // Same product — send all selected option value IDs (including injected swatch)
      const selectedOptions = Array.from(picker.querySelectorAll('fieldset input:checked'));
      optionValueIds = selectedOptions
        .map((input) => /** @type {HTMLElement} */ (input).dataset.optionValueId)
        .filter(Boolean);
    }

    const params = optionValueIds.length ? `option_values=${optionValueIds.join(',')}` : '';
    const requestUrl = `${productUrl.split('?')[0]}?section_id=section-rendering-quick-add&${params}`;

    // Abort any previous fetch
    this.#fetchAbortController?.abort();
    this.#fetchAbortController = new AbortController();

    fetch(requestUrl, { signal: this.#fetchAbortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        const newPicker = html.querySelector('variant-picker');
        if (!newPicker) return;

        // Detect product change (combined listings / product siblings)
        // Compare against #loadedProductId (the product whose fieldsets are in the DOM),
        // not picker.dataset.productId (which was already synced by #syncProductContext)
        const newProductId = /** @type {HTMLElement} */ (newPicker).dataset.productId;
        const isProductChange = newProductId && newProductId !== this.#loadedProductId;

        if (isProductChange) {
          this.#replaceVariantPickerContent(variantPickers, newPicker);
        } else {
          this.#updateAvailability(variantPickers, newPicker);
        }

        // Update variant picker JSON script tags
        const newScript = newPicker.querySelector('script[type="application/json"]');
        if (newScript?.textContent) {
          for (const vp of variantPickers) {
            const existingScript = vp.querySelector('script[type="application/json"]');
            if (existingScript) {
              existingScript.textContent = newScript.textContent;
            }
          }

          // Parse the variant data and update forms
          try {
            const variantData = JSON.parse(newScript.textContent);
            this.#syncFormsToVariant({ id: variantData.id, available: variantData.available });
          } catch {
            // Variant JSON parse failed — form keeps existing value
          }
        }

        // Update tracked product ID after fieldset replacement
        if (newProductId) {
          this.#loadedProductId = newProductId;
        }

        // Update cart notification templates from fetched response
        const newNotification = html.querySelector('[ref="cartNotificationContent"]');
        if (newNotification) {
          const templates = this.querySelectorAll('[ref="cartNotificationContent"]');
          for (const tpl of templates) {
            tpl.innerHTML = newNotification.innerHTML;
          }
        }

        // Update price display in modal/overlay from fetched response
        const newPrice = html.querySelector('[data-quick-add-price] [ref="priceContainer"]');
        if (newPrice) {
          const dialog = this.querySelector('quick-add-dialog');
          if (dialog) {
            const priceContainers = dialog.querySelectorAll('product-price [ref="priceContainer"]');
            for (const container of priceContainers) {
              container.innerHTML = newPrice.innerHTML;
            }
          }
        }

      })
      .catch((error) => {
        if (error.name !== 'AbortError') {
          console.error('[quick-add] fetchAndSyncContent: fetch failed', error);
        }
      });
  }

  /**
   * Updates variant-picker content for a combined listing product change.
   * Since option value IDs differ between products, we match visible buttons by
   * option position + value text, and update their IDs and availability.
   * Also replaces the hidden fieldsets in the variant-picker forms.
   *
   * @param {NodeListOf<Element>} variantPickers - Existing variant pickers in the DOM
   * @param {Element} newPicker - The variant-picker from the fetch response
   */
  #replaceVariantPickerContent(variantPickers, newPicker) {
    // Build a lookup from fetched response: { position -> { valueName -> { optionValueId, available, variantId, checked } } }
    const newFieldsets = newPicker.querySelectorAll('fieldset.variant-option');
    /** @type {Map<string, Map<string, { optionValueId: string, available: string, variantId: string, checked: boolean }>>} */
    const optionDataByPosition = new Map();

    for (const fs of newFieldsets) {
      const position = /** @type {HTMLElement} */ (fs).dataset.optionPosition;
      if (!position) continue;
      /** @type {Map<string, { optionValueId: string, available: string, variantId: string, checked: boolean }>} */
      const valueMap = new Map();
      for (const input of fs.querySelectorAll('input')) {
        const valueName = /** @type {HTMLInputElement} */ (input).value;
        const optionValueId = /** @type {HTMLElement} */ (input).dataset.optionValueId || '';
        const available = /** @type {HTMLElement} */ (input).dataset.optionAvailable || 'false';
        const variantId = /** @type {HTMLElement} */ (input).dataset.variantId || '';
        const checked = /** @type {HTMLInputElement} */ (input).hasAttribute('checked');
        valueMap.set(valueName, { optionValueId, available, variantId, checked });
      }
      optionDataByPosition.set(position, valueMap);
    }

    for (const vp of variantPickers) {
      // Update visible button inputs by matching position + value text
      const visibleFieldsets = vp.querySelectorAll('fieldset.variant-option');
      for (const fs of visibleFieldsets) {
        const position = /** @type {HTMLElement} */ (fs).dataset.optionPosition;
        if (!position) continue;
        const valueMap = optionDataByPosition.get(position);
        if (!valueMap) continue;

        for (const input of fs.querySelectorAll('input[data-option-value-id]')) {
          const valueName = /** @type {HTMLInputElement} */ (input).value;
          const newData = valueMap.get(valueName);
          if (newData) {
            /** @type {HTMLElement} */ (input).dataset.optionValueId = newData.optionValueId;
            /** @type {HTMLElement} */ (input).dataset.optionAvailable = newData.available;
            if (newData.variantId) {
              /** @type {HTMLElement} */ (input).dataset.variantId = newData.variantId;
            } else {
              delete /** @type {HTMLElement} */ (input).dataset.variantId;
            }
            if (newData.available === 'false') {
              /** @type {HTMLInputElement} */ (input).setAttribute('aria-disabled', 'true');
            } else {
              /** @type {HTMLInputElement} */ (input).removeAttribute('aria-disabled');
            }
            // Sync checked state to match the server's selected variant
            /** @type {HTMLInputElement} */ (input).checked = newData.checked;
            if (newData.checked) {
              input.setAttribute('checked', '');
            } else {
              input.removeAttribute('checked');
            }
          }
        }
      }

    }
  }

  /**
   * Updates availability on existing variant-picker inputs (same product, just different swatch).
   *
   * @param {NodeListOf<Element>} variantPickers - Existing variant pickers in the DOM
   * @param {Element} newPicker - The variant-picker from the fetch response
   */
  #updateAvailability(variantPickers, newPicker) {
    for (const vp of variantPickers) {
      syncAvailability(newPicker, vp);
    }
  }

  /**
   * Copies the product card's current main image to the modal image element.
   */
  #syncModalImage() {
    if (!this.#productCard) return;

    const cardImage = /** @type {HTMLImageElement | null} */ (
      this.#productCard.querySelector('.product-card-media__image')
    );
    const modalImage = /** @type {HTMLImageElement | null} */ (
      this.querySelector('.quick-add-modal__image-el')
    );

    if (cardImage && modalImage) {
      modalImage.src = cardImage.src;
      // Always clear srcset — the card image may have empty srcset, and the browser
      // would otherwise keep using the old srcset (showing the wrong color image)
      modalImage.srcset = cardImage.srcset || '';
    }
  }

  /**
   * Handles quick add button click (for multi-variant products)
   * @param {Event} event
   */
  handleClick = (event) => {
    event.preventDefault();

    if (this.quickAddStyle === 'overlay') {
      this.#toggleOverlay();
      return;
    }

    this.#openModal();
  };

  /**
   * Opens the per-card modal dialog
   */
  #openModal() {
    const dialogComponent = this.querySelector('quick-add-dialog');
    if (!(dialogComponent instanceof QuickAddDialog)) return;

    this.#syncToCurrentSwatch();
    this.#stayVisibleUntilDialogCloses(dialogComponent);
    dialogComponent.showDialog();
  }

  /**
   * Toggles the overlay visibility
   */
  #toggleOverlay() {
    const overlay = /** @type {HTMLElement | undefined} */ (this.refs.overlay);
    if (!overlay) return;

    if (!overlay.hidden) {
      this.closeOverlay();
      return;
    }

    // Sync forms to current swatch before showing
    this.#syncToCurrentSwatch();

    // Show overlay
    overlay.hidden = false;
    this.toggleAttribute('stay-visible', true);

    // Listen for cart updates to close overlay on success
    this.#overlayAbortController?.abort();
    this.#overlayAbortController = new AbortController();

    overlay.addEventListener(
      ThemeEvents.cartUpdate,
      (/** @type {CustomEvent} */ e) => {
        if (!e.detail?.data?.didError) {
          this.closeOverlay();
        }
      },
      { signal: this.#overlayAbortController.signal },
    );

    // Close on click outside
    const abortController = this.#overlayAbortController;
    requestAnimationFrame(() => {
      document.addEventListener(
        'click',
        (/** @type {MouseEvent} */ e) => {
          if (!overlay.contains(/** @type {Node} */ (e.target)) && !this.contains(/** @type {Node} */ (e.target))) {
            this.closeOverlay();
          }
        },
        { signal: abortController?.signal },
      );
    });
  }

  /**
   * Closes the overlay with animation
   */
  closeOverlay = async () => {
    const overlay = /** @type {HTMLElement | undefined} */ (this.refs.overlay);
    if (!overlay || overlay.hidden) return;

    this.#overlayAbortController?.abort();
    this.#overlayAbortController = null;

    overlay.classList.add('quick-add-overlay--closing');

    await onAnimationEnd(overlay, undefined, { subtree: false });

    overlay.hidden = true;
    overlay.classList.remove('quick-add-overlay--closing');
    this.toggleAttribute('stay-visible', false);
  };

  /**
   * Keeps the quick-add button visible while the dialog is open
   * @param {QuickAddDialog} dialogComponent
   */
  #stayVisibleUntilDialogCloses(dialogComponent) {
    this.toggleAttribute('stay-visible', true);

    dialogComponent.addEventListener(
      DialogCloseEvent.eventName,
      () => this.toggleAttribute('stay-visible', false),
      { once: true },
    );
  }

  /**
   * Close modal/overlay when viewport crosses the lg breakpoint
   */
  #closeOnViewportChange = () => {
    // Close modal
    const dialogComponent = this.querySelector('quick-add-dialog');
    if (dialogComponent instanceof QuickAddDialog) {
      dialogComponent.closeDialog();
    }

    // Close overlay
    this.closeOverlay();
  };
}

if (!customElements.get('quick-add-block')) {
  customElements.define('quick-add-block', QuickAddBlock);
}

/**
 * Quick add dialog component.
 * Per-card dialog that auto-closes on cart success and updates links on variant change.
 *
 * @extends {DialogComponent}
 */
class QuickAddDialog extends DialogComponent {
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener(ThemeEvents.cartUpdate, this.#handleCartUpdate, {
      signal: this.#abortController.signal,
    });
    this.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, {
      signal: this.#abortController.signal,
    });
    // Stop variant selected events from reaching product-card (which would try to sync to swatches)
    this.addEventListener(ThemeEvents.variantSelected, this.#stopPropagation, {
      signal: this.#abortController.signal,
    });
    // Stop click events from reaching product-card (which would navigate to PDP)
    this.addEventListener('click', this.#stopPropagation, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  /**
   * @param {Event} event
   */
  #stopPropagation = (event) => {
    event.stopPropagation();
  };

  /**
   * Close dialog immediately on successful cart add.
   * Must close synchronously (before the event reaches CartNotification on document)
   * because dialog.close() triggers light-dismiss of popover="auto" elements.
   * @param {CustomEvent} event
   */
  #handleCartUpdate = (event) => {
    if (event.detail?.data?.didError) return;

    const { dialog } = this.refs;
    if (!dialog.open) return;

    // Stop the event — we'll re-dispatch it after the dialog is closed
    event.stopPropagation();

    // Close dialog synchronously (before popover shows) to avoid light-dismiss
    // Don't restore scroll — let CartNotification.scrollToAnchor handle it (scrolls to top)
    document.body.style.width = '';
    document.body.style.position = '';
    document.body.style.top = '';

    dialog.close();
    this.dispatchEvent(new DialogCloseEvent());

    // Re-dispatch from the original source so cart-icon, cart-drawer, cart-notification all receive it
    const source = /** @type {Element | null} */ (event.target);
    if (source) {
      source.dispatchEvent(
        new CartAddEvent(event.detail.resource, event.detail.sourceId, event.detail.data),
      );
    }
  };

  /**
   * Update the "See full details" link and product title link with new variant ID
   * @param {CustomEvent} event
   */
  #handleVariantUpdate = (event) => {
    // Stop variant:update from reaching ProductCard — modal variant changes should not
    // trigger card-level handlers which expect section-rendering-product-card HTML.
    event.stopPropagation();

    // Sync size availability from response — variant picker skips morph in quick-add context
    const html = event.detail.data?.html;
    if (html) {
      const newPicker = html.querySelector('variant-picker');
      const picker = this.querySelector('variant-picker');
      if (newPicker && picker) syncAvailability(newPicker, picker);
    }

    const variantId = event.detail.resource?.id;
    if (!variantId) return;

    const detailsLink = /** @type {HTMLAnchorElement | null} */ (
      this.querySelector('.quick-add-modal__details-link')
    );

    if (detailsLink) {
      const url = new URL(detailsLink.href);
      url.searchParams.set('variant', String(variantId));
      detailsLink.href = url.toString();
    }
  };
}

if (!customElements.get('quick-add-dialog')) {
  customElements.define('quick-add-dialog', QuickAddDialog);
}
